/**
 * Include your custom JavaScript here.
 *
 * We also offer some hooks so you can plug your own logic. For instance, if you want to be notified when the variant
 * changes on product page, you can attach a listener to the document:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   var variant = event.detail.variant; // Gives you access to the whole variant details
 * });
 *
 * You can also add a listener whenever a product is added to the cart:
 *
 * document.addEventListener('product:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 *   var quantity = event.detail.quantity; // Get the quantity that was added
 * });
 *
 * If you just want to force refresh the mini-cart without adding a specific product, you can trigger the event
 * "cart:refresh" in a similar way (in that case, passing the quantity is not necessary):
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 */

/**
 * Product gallery preload, rewritten 2026-10-02 (Open Work Items Item 8, Item 15 fix 5).
 *
 * Original purpose (2026-09-11): product pages render every color's gallery
 * images into the DOM, but each uses lazysizes (class "Image--lazyLoad",
 * data-src instead of src), so a color the shopper has not looked at yet has
 * never been fetched and the first tap on it shows a visible lag. Preloading
 * the other images in the background makes color swaps instant. That purpose
 * is unchanged.
 *
 * What changed: the old version unveiled every gallery image on idle (timeout
 * 3 s), which competed with the main photo on first load and pulled every
 * photo of every color on slow connections. It now:
 *   - waits for the window load event, then requestIdleCallback (timeout
 *     5 s; setTimeout 3 s after load when unavailable);
 *   - skips entirely on saveData or a 2g / slow-2g connection;
 *   - at min-width 1008px (stacked gallery) unveils all lazy gallery images,
 *     as before;
 *   - below 1008px (carousel) unveils only one image per color: the images
 *     matching the distinct variant featured_media ids in [data-product-json].
 *
 * Uses lazysizes' public API (window.lazySizes.loader.unveil), as the
 * site-wide fallback below does. The first gallery image is a native eager
 * image (no Image--lazyLoad class), so it is never touched here.
 */
(function () {
  if (!/template-product/.test(document.body.className)) return;

  function shouldSkip() {
    var connection = navigator.connection;

    if (!connection) return false;

    return connection.saveData === true || connection.effectiveType === 'slow-2g' || connection.effectiveType === '2g';
  }

  // One entry per distinct variant featured_media id (a color usually has several variants, one per size).
  function getColorMediaIds() {
    var ids = [];

    try {
      var jsonElement = document.querySelector('[data-product-json]');

      if (!jsonElement) return ids;

      var data = JSON.parse(jsonElement.textContent);
      var variants = (data && data.product && data.product.variants) || [];

      variants.forEach(function (variant) {
        var media = variant && variant.featured_media;

        if (media && media.id && ids.indexOf(String(media.id)) === -1) {
          ids.push(String(media.id));
        }
      });
    } catch (error) {
      return [];
    }

    return ids;
  }

  function preloadRemainingGalleryImages() {
    if (!window.lazySizes || !window.lazySizes.loader || typeof window.lazySizes.loader.unveil !== 'function') {
      return;
    }

    if (window.matchMedia && window.matchMedia('(min-width: 1008px)').matches) {
      document.querySelectorAll('.Product__Gallery img.Image--lazyLoad').forEach(function (img) {
        window.lazySizes.loader.unveil(img);
      });

      return;
    }

    getColorMediaIds().forEach(function (mediaId) {
      var img = document.querySelector('.Product__Gallery .Product__SlideItem[data-media-id="' + mediaId + '"] img.Image--lazyLoad');

      if (img) {
        window.lazySizes.loader.unveil(img);
      }
    });
  }

  function schedulePreload() {
    if (shouldSkip()) return;

    if ('requestIdleCallback' in window) {
      requestIdleCallback(preloadRemainingGalleryImages, { timeout: 5000 });
    } else {
      setTimeout(preloadRemainingGalleryImages, 3000);
    }
  }

  if (document.readyState === 'complete') {
    schedulePreload();
  } else {
    window.addEventListener('load', schedulePreload);
  }
})();

/**
 * Seasonal collections carousel toggle, 2026-09-19.
 *
 * sections/seasonal-collections-carousel.liquid is not one of the section
 * types theme.min.js registers (see the sections.register(...) calls in that
 * file), so there is no built-in JS driving its Flickity carousels or its
 * Fall/Halloween toggle buttons. This block does both by hand.
 *
 * Both panels' carousels are initialized on load, including the one that
 * starts hidden. Flickity measures its container's width at init time, and
 * an element hidden via "display: none" measures zero width, so the hidden
 * panel's carousel initializes broken. Dispatching a "resize" event right
 * after the panel is revealed forces Flickity to recalculate against the
 * now-visible container, which is why that dispatch has to happen first,
 * before anything else runs in the click handler.
 */
(function () {
  if (typeof Flickity === 'undefined') return;

  var sections = document.querySelectorAll('[data-section-type="seasonal-collections-carousel"]');

  sections.forEach(function (section) {
    section.querySelectorAll('[data-flickity-config]').forEach(function (carouselElement) {
      new Flickity(carouselElement, JSON.parse(carouselElement.getAttribute('data-flickity-config')));
    });

    var toggleButtons = section.querySelectorAll('[data-seasonal-toggle]');

    toggleButtons.forEach(function (button) {
      button.addEventListener('click', function () {
        if (button.classList.contains('is-active')) {
          return;
        }

        var panelToShow = section.querySelector('#' + button.getAttribute('aria-controls'));

        toggleButtons.forEach(function (item) {
          item.classList.remove('is-active');
          item.setAttribute('aria-selected', 'false');
        });

        button.classList.add('is-active');
        button.setAttribute('aria-selected', 'true');

        section.querySelectorAll('[data-seasonal-panel]').forEach(function (panel) {
          panel.classList.add('is-hidden');
        });

        panelToShow.classList.remove('is-hidden');

        // Required: forces Flickity to recalculate now that the panel is visible (see comment above).
        window.dispatchEvent(new Event('resize'));
      });
    });
  });
})();

/**
 * Site-wide lazy-load fallback, 2026-09-19.
 *
 * lazySizes' own automatic viewport detection does not trigger anywhere
 * on this site, confirmed by direct testing: .Image--lazyLoad elements
 * never transition to .Image--lazyLoaded on their own, on any page.
 * Direct calls to window.lazySizes.loader.unveil() do work reliably,
 * confirmed by the existing gallery preload block above, which
 * successfully loads every product gallery image this exact way. This
 * block generalizes that same proven technique to every lazy image on
 * the site, using our own IntersectionObserver to call unveil() when an
 * image nears the viewport, preserving genuine lazy loading rather than
 * loading everything eagerly on page load.
 */
(function () {
  // Temporary diagnostic tool, not meant to be removed casually: append
  // ?skip_lazy_fallback=1 to a page URL to bypass this fallback entirely,
  // so we can check whether lazySizes' own viewport detection has started
  // working on its own without this fix masking the result.
  if (/[?&]skip_lazy_fallback=1(&|$)/.test(window.location.search)) return;

  if (!('IntersectionObserver' in window)) return;

  // lazysizes.min.js loads async and this file loads defer, so there's no
  // guarantee which finishes first; poll briefly for window.lazySizes to
  // be ready rather than giving up if we happen to run first.
  var POLL_INTERVAL_MS = 50;
  var POLL_TIMEOUT_MS = 2000;
  var elapsed = 0;

  function isLazySizesReady() {
    return !!(window.lazySizes && window.lazySizes.loader && typeof window.lazySizes.loader.unveil === 'function');
  }

  function waitForLazySizes() {
    if (isLazySizesReady()) {
      setup();
      return;
    }

    elapsed += POLL_INTERVAL_MS;
    if (elapsed >= POLL_TIMEOUT_MS) return;

    setTimeout(waitForLazySizes, POLL_INTERVAL_MS);
  }

  function setup() {
    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          window.lazySizes.loader.unveil(entry.target);
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '200px 0px' });

    function observeAll() {
      document.querySelectorAll('img.Image--lazyLoad').forEach(function (img) {
        observer.observe(img);
      });
    }

    observeAll();

    // Catches images added after initial page load, e.g. AJAX cart updates.
    var mutationObserver = new MutationObserver(observeAll);
    mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  waitForLazySizes();
})();
